import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { TextBlock } from '@hotelplan/components.common.text-block';
import { BsButton } from '@hotelplan/core.basis.bs-button';
import { BsLink } from '@hotelplan/core.basis.bs-link';
import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { FdrRoute } from '@hotelplan/fdr.regular.basis.fdr-route';
import { useFeatureToggle } from '@hotelplan/libs.feature-toggle';
import { FdrLink } from '@hotelplan/supergraph-api';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import FdrBookmarkCatalog from 'components/domain/fdr-site-tool/fdr-bookmark-catalog';
import FdrBookmarkGroupShareTool from 'components/domain/fdr-site-tool/fdr-bookmark-group-share-site-tool';
import { TextBlockContainer } from 'components/domain/text-block/TextBlockContainer';
import { trackAddNewBookmarkGroup } from 'components/domain/tracking/bookmarkTracking';
import { fdrBookmarkPageDefaultPagination } from 'components/page/bookmark/bookmark.vars';
import { FeatureList } from 'config/featureList';
import { useFdrBookmarkListLazyQuery } from 'schemas/query/bookmark/fdr-bookmark-list.generated';
import { useBookmarkContext } from './context/fdr-bookmark-context';
import {
  BookmarkModalPopupType,
  useBookmarkModalPopupContext,
} from './context/fdr-bookmark-modal-popup-context';
import FdrBookmarkGroupList from './fdr-bookmark-group-list';
import { BookmarkSection } from './fdr-bookmark-section-list';
import { BookmarkSectionHeaderLoading } from './fdr-bookmark-section.styles';
import { IBookmarkGroupState } from './fdr-bookmark-types';

const Link = styled(BsLink)(({ theme }) => theme.link.uiPrimary);

const WishlistTextBlock = styled(TextBlock)({
  width: '100%',
  maxWidth: '100%',
});

const EmptyWishlistWrapper = styled(TextBlockContainer)(
  ({ theme: { colors, space, media } }) => ({
    maxWidth: '600px',
    padding: '25px 39px',
    border: `2px solid ${colors.primary}`,
    textAlign: 'center',
    margin: `${space[6]} auto`,
    [media.tablet]: {
      margin: `${space[9]} auto`,
    },
  })
);

const EmptyWishlistTextBlock = styled.div(({ theme: { space } }) => ({
  marginBottom: space[4],
}));

const BookmarkGroupCreateNewLinkWrapper = styled.div({
  '.link': {
    display: 'inline-flex',
  },
});

const BookmarkTitleWrapper = styled.div(({ theme: { space } }) =>
  sx2CssThemeFn({
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginTop: space[8],
    marginBottom: ['24px', '40px'],
    '.bookmark-site-tools-button-wrapper': {
      marginLeft: 'auto',
    },
    '.share-overlay': {
      top: [0, '8px'],
    },
    h1: {
      margin: 0,
    },
  })
);

const BookmarkGroupNameWrapper = styled(BookmarkTitleWrapper)({
  marginTop: 0,
  marginBottom: 0,
  paddingTop: '40px',
  paddingBottom: '15px',
  '.share-overlay': {
    top: '40px',
  },
});

interface IBookmarkItemsWrapper {
  title: string;
  main: string;
  buttonLink?: FdrLink;
  buttonText?: string;
}

const BOOKMARK_GROUPS_PER_PAGE = 5;

const FdrBookmarkItemsWrapper: React.FC<IBookmarkItemsWrapper> = props => {
  const { buttonLink, buttonText, title, main } = props;
  const [t, { language }] = useTranslation(['search', 'common']);

  const [activeBookmarkGroup, setActiveBookmarkGroup] =
    useState<IBookmarkGroupState | null>(null);

  const [
    getBookmarkList,
    { data: bookmarkListData, loading: bookmarkListLoading },
  ] = useFdrBookmarkListLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const { isEnabled } = useFeatureToggle();
  const multipleBookmarksEnabled = isEnabled(FeatureList.MULTIPLE_BOOKMARKS);
  const bookmarkContext = useBookmarkContext();
  const bookmarkModalPopupContext = useBookmarkModalPopupContext();

  const { channelType } = useAuthentication();
  const isB2B = channelType === AuthChannelType.B2B;

  useEffect(() => {
    const activeGroup = bookmarkContext.bookmarkGroups.find(
      i => i.id === activeBookmarkGroup?.id
    );

    if (activeGroup) {
      setActiveBookmarkGroup(activeGroup);
    } else if (bookmarkContext.bookmarkGroups.length) {
      setActiveBookmarkGroup(bookmarkContext.bookmarkGroups[0]);
    }
  }, [bookmarkContext.bookmarkGroups, activeBookmarkGroup]);

  useEffect(() => {
    if (activeBookmarkGroup) {
      getBookmarkList({
        variables: {
          groupId: activeBookmarkGroup.id,
          pagination: fdrBookmarkPageDefaultPagination,
        },
      });
    }
  }, [activeBookmarkGroup, getBookmarkList]);

  if (bookmarkContext.loading) {
    return (
      <BookmarkSectionHeaderLoading>
        <span className="title">{t('search:loading')}</span>
      </BookmarkSectionHeaderLoading>
    );
  }

  if (
    !bookmarkContext.loading &&
    !bookmarkListLoading &&
    !bookmarkContext.bookmarkGroups.find(g => g.items.length > 0)
  ) {
    return (
      <EmptyWishlistWrapper>
        <EmptyWishlistTextBlock dangerouslySetInnerHTML={{ __html: main }} />
        {buttonLink && buttonText && (
          <FdrRoute link={buttonLink}>
            <BsLink>
              <BsButton>{buttonText}</BsButton>
            </BsLink>
          </FdrRoute>
        )}
      </EmptyWishlistWrapper>
    );
  }

  return (
    <>
      <BookmarkTitleWrapper>
        <WishlistTextBlock title={title} text={''} />
        {!multipleBookmarksEnabled && activeBookmarkGroup && (
          <FdrBookmarkGroupShareTool
            activeBookmarkGroup={activeBookmarkGroup}
          />
        )}
      </BookmarkTitleWrapper>
      {multipleBookmarksEnabled && activeBookmarkGroup && (
        <>
          <BookmarkGroupCreateNewLinkWrapper>
            <Link
              onClick={event => {
                event.preventDefault();
                trackAddNewBookmarkGroup(language);

                bookmarkModalPopupContext.open({
                  type: BookmarkModalPopupType.CREATE_BOOKMARK_GROUP,
                });
              }}
              icon={{ name: 'plus-borderless' }}
            >
              <span className="link-text">
                {isB2B
                  ? t('common:bookmark.group.create.title.b2b')
                  : t('common:bookmark.group.create.title')}
              </span>
            </Link>
          </BookmarkGroupCreateNewLinkWrapper>
          <FdrBookmarkGroupList
            bookmarkGroups={bookmarkContext.bookmarkGroups}
            activeGroup={activeBookmarkGroup}
            page={{
              page: 0,
              perPage: BOOKMARK_GROUPS_PER_PAGE,
              total: bookmarkContext.bookmarkGroups.length,
            }}
            onSelect={bookmarkGroup => {
              setActiveBookmarkGroup(bookmarkGroup);
            }}
          />
          <BookmarkGroupNameWrapper>
            <h2>{activeBookmarkGroup.name}</h2>
            <FdrBookmarkGroupShareTool
              activeBookmarkGroup={activeBookmarkGroup}
            />
            {isB2B ? (
              <FdrBookmarkCatalog
                activeGroup={activeBookmarkGroup}
                title={activeBookmarkGroup.name}
              />
            ) : null}
          </BookmarkGroupNameWrapper>
        </>
      )}
      <div>
        <BookmarkSection
          key={'bookmark-section-adventure-travels'}
          activeGroup={activeBookmarkGroup}
          items={bookmarkListData?.atBookmarks.bookmarks}
          headerTranslationId={'bookmark:adventureTrips'}
          pdfSection
        />
        <BookmarkSection
          key={'bookmark-section-roundtrips'}
          activeGroup={activeBookmarkGroup}
          items={bookmarkListData?.rtBookmarks.bookmarks}
          headerTranslationId={'bookmark:roundtrips'}
          pdfSection
        />
        <BookmarkSection
          key={'bookmark-section-hotels'}
          activeGroup={activeBookmarkGroup}
          items={bookmarkListData?.hotelBookmarks.bookmarks}
          headerTranslationId={'bookmark:hotels'}
          pdfSection
        />
        <BookmarkSection
          key={'bookmark-section-geo'}
          activeGroup={activeBookmarkGroup}
          items={bookmarkListData?.geoBookmarks.bookmarks}
          headerTranslationId={'bookmark:geoObjects'}
          pdfSection
        />
        <BookmarkSection
          key={'bookmark-section-themes'}
          activeGroup={activeBookmarkGroup}
          items={bookmarkListData?.themeBookmarks.bookmarks}
          headerTranslationId={'bookmark:themes'}
          pdfSection
        />
      </div>
    </>
  );
};

export default FdrBookmarkItemsWrapper;
